import { css } from '@emotion/css';
import { monoFont } from '@medcom/shared-ui/styles/globalCss.ts';
import { Alert, Button, Flex, Input, Modal, Segmented, Typography, theme } from 'antd';
import { useEffect, useState } from 'react';
import { useDebounce } from 'use-debounce';
import { LabelImage } from '../../../components/LabelImage.tsx';
import { useLabelPreview } from '../../../hooks/useLabelPreview.ts';
import { type LabelType, placeholdersInTemplate } from '../../../utils/labelPlaceholders.ts';
import { LABEL_PRESETS, type PresetId } from '../../../utils/labelPresets.ts';

type Props = {
  open: boolean;
  onClose: () => void;
  labelType: LabelType;
  zpl: string;
  width: number;
  height: number;
  /** Values known from the form, e.g. the lab's own short name, override the presets. */
  fixedValues?: Record<string, string>;
};

export const LabelPreviewModal = ({ open, onClose, labelType, width, height, ...rest }: Props) => (
  <Modal
    open={open}
    onCancel={onClose}
    width={960}
    footer={null}
    destroyOnHidden
    title={
      <Flex vertical>
        <span>Etiketten-Vorschau</span>
        <Typography.Text type="secondary">
          {labelType === 'ORDER' ? 'Laboretikett' : 'Zuweiser-Etikett'} · {width} × {height} mm
        </Typography.Text>
      </Flex>
    }
  >
    {open && <PreviewContent labelType={labelType} width={width} height={height} onClose={onClose} {...rest} />}
  </Modal>
);

const PreviewContent = ({ labelType, zpl, width, height, fixedValues, onClose }: Omit<Props, 'open'>) => {
  const { token } = theme.useToken();
  const placeholders = placeholdersInTemplate(zpl, labelType);
  const valuesOf = (id: PresetId) => ({ ...LABEL_PRESETS.find(p => p.id === id)?.values, ...fixedValues });

  const [presetId, setPresetId] = useState<PresetId>('short');
  const [values, setValues] = useState(() => valuesOf('short'));
  const [custom, setCustom] = useState(false);
  const [debouncedValues] = useDebounce(values, 400);
  const { objectUrl, error, loading, render } = useLabelPreview();

  useEffect(() => {
    render({ zpl, width, height, labelType, values: debouncedValues });
  }, [render, zpl, width, height, labelType, debouncedValues]);

  const selectPreset = (id: PresetId) => {
    setPresetId(id);
    setValues(valuesOf(id));
    setCustom(false);
  };

  const layoutStyle = css`
    display: grid;
    grid-template-columns: 300px minmax(0, 1fr);
    gap: ${token.marginLG}px;
    align-items: start;
    margin-block: ${token.marginLG}px;
  `;
  const fieldStyle = css`
    display: flex;
    flex-direction: column;
    gap: ${token.marginXXS}px;
    margin-bottom: ${token.marginSM}px;
  `;
  const keyStyle = css`
    font-size: ${token.fontSizeSM}px;
    color: ${token.colorLink};
  `;
  const stageStyle = css`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: ${token.marginSM}px;
    padding: ${token.paddingLG}px;
    background: ${token.colorFillQuaternary};
    border: 1px solid ${token.colorBorderSecondary};
    border-radius: ${token.borderRadiusLG}px;
  `;
  const smallStyle = css`
    font-size: ${token.fontSizeSM}px;
  `;

  return (
    <>
      <Flex align="center" gap={token.marginSM}>
        <Typography.Text type="secondary">Beispielwerte</Typography.Text>
        <Segmented<PresetId> value={presetId} onChange={selectPreset} options={LABEL_PRESETS.map(p => ({ label: p.label, value: p.id }))} />
        {custom && <Typography.Text type="secondary">Eigene Werte</Typography.Text>}
      </Flex>

      <div className={layoutStyle}>
        <div>
          {placeholders.length === 0 && <Typography.Text type="secondary">Die Vorlage enthält keine Platzhalter.</Typography.Text>}
          {placeholders.map(p => (
            <div key={p.key} className={fieldStyle}>
              <label htmlFor={`label-preview-${p.key}`}>
                <Flex gap={token.marginXS} align="baseline">
                  <span className={`${monoFont} ${keyStyle}`}>%{p.key}%</span>
                  <Typography.Text type="secondary" className={smallStyle}>
                    {p.description}
                  </Typography.Text>
                </Flex>
              </label>
              <Input
                id={`label-preview-${p.key}`}
                value={values[p.key] ?? ''}
                placeholder="leer"
                onChange={e => {
                  setValues(v => ({ ...v, [p.key]: e.target.value }));
                  setCustom(true);
                }}
              />
            </div>
          ))}
        </div>

        <div className={stageStyle}>
          {error ? (
            <Alert
              type="error"
              showIcon
              title={error.title}
              description={error.detail && <span className={monoFont}>{error.detail}</span>}
            />
          ) : (
            objectUrl && <LabelImage src={objectUrl} dimmed={loading} />
          )}
          <Typography.Text type="secondary" className={smallStyle}>
            {loading ? 'wird gerendert…' : 'Darstellung 1,5-fach · 8 dpmm'}
          </Typography.Text>
        </div>
      </div>

      <Flex justify="space-between">
        <Button onClick={() => selectPreset(presetId)}>Auf Beispielwerte zurücksetzen</Button>
        <Button type="primary" onClick={onClose}>
          Schließen
        </Button>
      </Flex>
    </>
  );
};
